import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import useExchangeRate from '../hooks/useExchangeRate';

const API = 'https://affinityconcept.tech/api';

const TYPES = ['Savings', 'Bonds', 'Funds', 'Gold', 'ETF', 'Other'];
const fmtAED = (n) => `AED ${Math.round(n).toLocaleString('en-GB')}`;
const emptyForm = { name: '', investment_type: 'Savings', amount_invested: '', current_value: '', currency: 'AED', start_date: '', notes: '' };

export default function OtherInvestments({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { toAED } = useExchangeRate();

  const headers = { Authorization: `Bearer ${token}` };

  const load = () => axios.get(`${API}/other-investments`, { headers }).then(r => setItems(r.data));

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
    load();
  }, []);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (it) => {
    setEditId(it.id);
    setForm({
      name: it.name || '', investment_type: it.investment_type || 'Savings',
      amount_invested: it.amount_invested ?? '', current_value: it.current_value ?? '',
      currency: it.currency || 'AED',
      start_date: it.start_date ? it.start_date.slice(0, 10) : '',
      notes: it.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount_invested: parseFloat(form.amount_invested),
      current_value: parseFloat(form.current_value),
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
    };
    if (editId) await axios.put(`${API}/other-investments/${editId}`, payload, { headers });
    else await axios.post(`${API}/other-investments`, payload, { headers });
    await load();
    setShowForm(false);
    setEditId(null);
    setForm(emptyForm);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this investment?')) return;
    await axios.delete(`${API}/other-investments/${id}`, { headers });
    load();
  };

  const investedAED = (it) => toAED(it.amount_invested || 0, it.currency);
  const valueAED = (it) => toAED(it.current_value || 0, it.currency);

  const totalInvested = items.reduce((s, it) => s + investedAED(it), 0);
  const totalValue = items.reduce((s, it) => s + valueAED(it), 0);
  const totalPL = totalValue - totalInvested;

  const Card = ({ label, value, accent }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-gray-900'}`}>{value}</p>
    </div>
  );

  const inputCls = "border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500";

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
        <Link to="/investments" className="text-sm text-gray-500 hover:text-green-600">← Back to Overview</Link>

        <div className="flex justify-between items-center mt-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🏦</div>
            <h1 className="text-2xl font-bold text-gray-900">Other Investments</h1>
          </div>
          <button onClick={openAdd}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium">+ Add</button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card label="Total Invested" value={fmtAED(totalInvested)} />
          <Card label="Current Value" value={fmtAED(totalValue)} />
          <Card label="Total P&L" value={`${totalPL >= 0 ? '+' : ''}${fmtAED(totalPL)}`}
            accent={totalPL >= 0 ? 'text-green-500' : 'text-red-500'} />
        </div>

        {/* Add / Edit form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-base font-semibold mb-4">{editId ? 'Edit Investment' : 'New Investment'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Name" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} />
              <select value={form.investment_type} onChange={e => setForm({ ...form, investment_type: e.target.value })} className={inputCls}>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
              <input required type="number" step="any" placeholder="Amount invested" value={form.amount_invested}
                onChange={e => setForm({ ...form, amount_invested: e.target.value })} className={inputCls} />
              <input required type="number" step="any" placeholder="Current value" value={form.current_value}
                onChange={e => setForm({ ...form, current_value: e.target.value })} className={inputCls} />
              <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={inputCls}>
                <option>AED</option>
                <option>USD</option>
              </select>
              <input type="date" value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputCls} />
              <input placeholder="Notes" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inputCls} md:col-span-2`} />
              <div className="flex gap-3 md:col-span-2">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">Save</button>
                <button type="button" onClick={() => { setShowForm(false); setEditId(null); }}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Invested</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Current Value</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">P&L</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Start Date</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No other investments yet.</td></tr>
                )}
                {items.map(it => {
                  const invested = investedAED(it);
                  const value = valueAED(it);
                  const pl = value - invested;
                  const plPct = invested > 0 ? (pl / invested) * 100 : 0;
                  return (
                    <tr key={it.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-4 text-sm font-medium text-gray-900">{it.name}</td>
                      <td className="p-4">
                        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">{it.investment_type}</span>
                      </td>
                      <td className="p-4 text-sm text-gray-700 text-right">{it.currency} {it.amount_invested?.toLocaleString('en-GB')}</td>
                      <td className="p-4 text-sm font-semibold text-gray-900 text-right">{it.currency} {it.current_value?.toLocaleString('en-GB')}</td>
                      <td className={`p-4 text-sm font-medium text-right ${pl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {pl >= 0 ? '+' : ''}{fmtAED(pl)}
                        <span className="block text-xs">{plPct >= 0 ? '+' : ''}{plPct.toFixed(1)}%</span>
                      </td>
                      <td className="p-4 text-sm text-gray-500 hidden md:table-cell">
                        {it.start_date ? new Date(it.start_date).toLocaleDateString('en-GB') : '-'}
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(it)} className="text-gray-400 hover:text-green-600 text-sm mr-2">✏️</button>
                        <button onClick={() => handleDelete(it.id)} className="text-gray-400 hover:text-red-600 text-sm">🗑️</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
